import { Loading } from '@core/components/Loading';
import Wizard from '@core/components/Wizard';
import { useReduxLoading } from '@core/hooks';
import FuseMessage from '@fuse/core/FuseMessage';
import FusePageSimpleSidebar from '@fuse/core/FusePageSimple/FusePageSimpleSidebar';
import FuseScrollbars from '@fuse/core/FuseScrollbars';
import { Button, Grid, Hidden, makeStyles } from '@material-ui/core';
import { ArrowBack, ArrowForward } from '@material-ui/icons';
import empresaOnboardingApi from 'app/services/api/empresaOnboardngApi';
import { GlobalContext } from 'app/store/globalcontext';
import React, { useContext, useRef } from 'react';
import InformativoOnboarding from './components/InformativoOnboarding';
import { OnboardingContext } from './OnboardingContext';

const headerHeight = 180;
const toolbarHeight = 180;
const drawerWidth = 236;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    position: 'relative',
    flex: '1 0 auto',
    height: 'auto',
    backgroundColor: theme.palette.background.default,
  },
  innerScroll: {
    flex: '1 1 auto',
    height: '100%',
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    zIndex: 2,
    maxWidth: '100%',
    minWidth: 0,
    height: '100%',
    backgroundColor: theme.palette.background.default,
  },
  header: {
    height: headerHeight,
    minHeight: headerHeight,
    display: 'flex',
    color: theme.palette.secondary.contrastText,
    backgroundSize: 'cover',
    backgroundColor: theme.palette.secondary.dark,
  },
  topBg: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: headerHeight,
    pointerEvents: 'none',
  },
  contentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    overflow: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    background: '#fff',
  },
  fundoApresentacao: {
    background: '#fff url("assets/images/onboarding/fundo.svg") no-repeat center center fixed',
  },
  toolbar: {
    height: toolbarHeight,
    minHeight: toolbarHeight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    width: '100%',
    overflowY: 'scroll',
  },
  sidebarWrapper: {
    overflow: 'hidden',
    backgroundColor: 'transparent',
    zIndex: 10000,
    '&.permanent': {
      [theme.breakpoints.up('sm')]: {
        position: 'relative',
      },
    },
  },
  sidebar: {
    position: 'absolute',
    '&.permanent': {
      [theme.breakpoints.up('sm')]: {
        backgroundColor: '#f8fafc',
        color: '#464646',
        position: 'relative',
      },
    },
    width: drawerWidth,
    height: '100%',
  },
  leftSidebar: {
    [theme.breakpoints.up('lg')]: {
      borderRight: `none`,
      borderLeft: 0,
      zIndex: 5,
      position: 'absolute',
      backgroundColor: '#f8fafc',
    },
  },
  btnAvancar: {
    height: '100%',
    borderRadius: '48px',
    fontSize: '16px',
    textTransform: 'capitalize',
    backgroundColor: '#2e62b8',
    marginRight: '1%',
  },
  btnPularEtapa: {
    height: '100%',
    borderRadius: '48px',
    fontSize: '16px',
    float: 'right',
    textTransform: 'none',
    color: '#65666f',
  },

  btnvoltar: {
    zIndex: 1000,
    height: '100%',
    borderRadius: '48px',
    marginLeft: '10%',
    paddingLeft: '1%',
    fontSize: '12px',
    backgroundColor: '#f5f5f5',
    borderColor: '#f5f5f5',
  },
  informativo: {
    position: 'fixed',
    zIndex: 3000,
    float: 'right',
    bottom: '11%',
    width: '90%',
  },
}));

const OnboardingLayout = (props) => {
  const rootRef = useRef(null);
  const classes = useStyles(useStyles);

  const [etapa, setEtapa] = useContext(OnboardingContext).etapa;
  const [uploading] = useContext(OnboardingContext).uploading;
  const [loading, setLoading] = useContext(OnboardingContext).loading;
  const [steps, setSteps] = useContext(OnboardingContext).steps;
  const [, setAtualizarEtapa] = useContext(OnboardingContext).atualizarEtapa;
  const [empresa] = useContext(GlobalContext).empresa;

  useReduxLoading(loading);

  const etapas = [
    'INICIO',
    'DADOS_CADASTRAIS',
    'LOGOMARCA',
    'INFORMACOES_FISCAIS',
    'CONTA_BANCARIA',
    'CERTIFICADO_DIGITAL',
    'FINALIZADO',
  ];

  const voltar = () => {
    const proximaEtapa = etapas[etapas.indexOf(etapa) - 1];
    setEtapa(proximaEtapa);
    steps.forEach((step) => {
      if (step.value === proximaEtapa) {
        step.pularEtapa = false;
      }
    });
    setSteps(steps);
  };

  const exibeBotaoVoltar = () => {
    return (
      <>
        {etapa === 'LOGOMARCA' ||
        etapa === 'INFORMACOES_FISCAIS' ||
        etapa === 'CONTA_BANCARIA' ||
        etapa === 'CERTIFICADO_DIGITAL' ? (
          <Button
            title="Retornar à página anterior"
            className={classes.btnvoltar}
            disabled={uploading}
            size="large"
            variant="outlined"
            onClick={voltar}
            endIcon={
              uploading ? (
                <ArrowBack style={{ color: 'rgb(146, 176, 225)' }} />
              ) : (
                <ArrowBack style={{ color: '#2e62b8' }} />
              )
            }
          />
        ) : null}
      </>
    );
  };

  const exibeBotaoPularEtapa = () => {
    return (
      <>
        {etapa === 'LOGOMARCA' || etapa === 'CONTA_BANCARIA' || etapa === 'CERTIFICADO_DIGITAL' ? (
          <Button
            size="large"
            disabled={uploading}
            title="Pular essa etapa"
            className={classes.btnPularEtapa}
            onClick={async () => {
              setLoading(true);
              steps.forEach((step) => {
                if (step.value === etapa) {
                  step.pularEtapa = true;
                }
              });
              setSteps(steps);
              const resp = await empresaOnboardingApi.atualizar(empresa.id, etapa, {});
              if (resp) {
                setAtualizarEtapa(true);
              }
              setLoading(false);
            }}
            variant="text"
          >
            Pular essa etapa
          </Button>
        ) : null}
      </>
    );
  };

  const avancaAba = () => {
    /*   steps.forEach((step) => {
      if (step.value === etapa) {
        step.pularEtapa = false;
      }
    });
    setSteps(steps); */
  };

  const exibeBotaoAvancar = () => {
    return (
      <>
        {etapa !== 'INICIO' && etapa !== 'FINALIZADO' ? (
          <Button
            className={classes.btnAvancar}
            size="large"
            disabled={uploading}
            title="Avançar"
            variant="contained"
            onClick={(e) => {
              if (props.onSubmit) {
                props.onSubmit();
                avancaAba();
              }
            }}
            color="primary"
            endIcon={<ArrowForward style={{ color: '#92b0e1' }} />}
          >
            Avançar
          </Button>
        ) : null}
      </>
    );
  };

  const exibeImagemFundo = () => {
    return etapa === 'INICIO' || etapa === 'FINALIZADO'
      ? `${classes.contentWrapper} ${classes.fundoApresentacao}`
      : classes.contentWrapper;
  };

  const exibeInformativo = () => {
    if (etapa === 'DADOS_CADASTRAIS') {
      return (
        <InformativoOnboarding titulo="Por que é importante preencher estes dados?">
          Completar os dados da sua empresa é importante para que você aproveite as várias funcionalidades que temos
          para te oferecer, como por exemplo, emissão de boletos bancários e de notas fiscais de produtos e serviços
          para os seus clientes.
          <br />
          <br />
          Mantenha os dados da sua empresa sempre atualizados.
        </InformativoOnboarding>
      );
    }
    if (etapa === 'LOGOMARCA') {
      return (
        <InformativoOnboarding titulo="Por que é importante enviar sua logomarca?">
          A logomarca deve ser anexada para que seus clientes reconheçam a sua empresa nos boletos bancários, orçamentos
          enviados, pedidos de vendas e nos relatórios que serão emitidos pelo sistema.
        </InformativoOnboarding>
      );
    }
    if (etapa === 'INFORMACOES_FISCAIS') {
      return (
        <InformativoOnboarding titulo="Por que é importante preencher estes dados?">
          O FatureMais utilizará estas informações para comunicar com a prefeitura da sua cidade e/ou com a Secretraria
          da Fazenda e, assim, emitir sua nota fiscal.
        </InformativoOnboarding>
      );
    }
    if (etapa === 'CONTA_BANCARIA') {
      return (
        <InformativoOnboarding titulo="Por que é importante preencher estes dados?">
          Finalizando o cadastro da sua conta, você dá o primeiro passo para sua empresa poder integrar o seu banco com
          o FatureMais. Com isso, você poderá controlar tudo o que foi pago e recebido em todas as contas cadastradas no
          sistema. Além disso, você poderá visualizar essas informações detalhadas utilizando relatórios gerenciais.
        </InformativoOnboarding>
      );
    }
    if (etapa === 'CERTIFICADO_DIGITAL') {
      return (
        <InformativoOnboarding titulo="Por que é importante enviar seu certificado?">
          O certificado digital A1 é necessário para assinar eletronicamente as notas fiscais emitidas no sistema. Ele é
          um arquivo eletrônico que identifica a empresa e garante proteção às suas transações online.
        </InformativoOnboarding>
      );
    }
  };

  return (
    <div className={classes.wrapper}>
      {props.leftSidebarContent ? (
        <FusePageSimpleSidebar
          position="left"
          content={props.leftSidebarContent}
          variant="permanent"
          classes={classes}
          rootRef={rootRef}
        />
      ) : null}
      <FuseScrollbars className={exibeImagemFundo()}>
        {props.wizardStep >= 0 ? (
          <div className={classes.toolbar}>
            <Wizard steps={steps} activeStep={props.wizardStep} />
          </div>
        ) : null}
        <div className="flex align-center justify-center container z-10 h-full">
          {props.content && (
            <div className={classes.content} style={{ paddingBottom: '10%' }}>
              {props.content}
            </div>
          )}
          <br />
          <br />
          <Hidden smDown>
            <div className={classes.informativo}>
              <Grid container>
                <Grid item lg={5} md={4} sm={4} xs={false} />
                <Grid item lg={2} md={1} sm={1} xs={false} />
                <Grid item lg={4} md={6} sm={6} xs={false}>
                  {exibeInformativo()}
                </Grid>
                <Grid item lg={1} md={1} sm={1} xs={false} />
              </Grid>
            </div>
          </Hidden>
        </div>

        <header className="MuiPaper-root MuiPaper-elevation MuiAppBar-root MuiAppBar-positionFixed relative z-20 sticky bottom-0 mui-fixed">
          <Grid container>
            <Grid container style={{ marginBottom: '1.5%' }}>
              <Grid item lg={5} md={5} sm={4} xs={3}>
                {exibeBotaoVoltar()}
              </Grid>
              <Grid item lg={5} md={3} sm={4} xs={4}>
                {exibeBotaoPularEtapa()}
              </Grid>
              <Grid item lg={1} md={3} sm={3} xs={4}>
                {exibeBotaoAvancar()}
              </Grid>
              <Grid item lg={1} md={1} sm={1} xs={1} />
            </Grid>
          </Grid>
        </header>

        <FuseMessage />
      </FuseScrollbars>
      <Loading open={loading} />
    </div>
  );
};

export default OnboardingLayout;
